import { Table, Flex, Text, Box } from '@radix-ui/themes';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

import TextField from '../../../shared/TextField/TextField.jsx';
import ActionButtons from './ActionButtons.jsx';
import AutoExpandTextarea from './AutoExpandTextarea.jsx';
import styles from './NewGoalForm.module.css';

const GoalRow = ({
  goal,
  index,
  year,
  editingId,
  editData,
  setEditData,
  startEditing,
  saveEdit,
  cancelEdit,
  deleteGoal,
  copyGoal,
  recentlyMovedGoalId,
  currentTargetPosition,
  activeGoal,
}) => {
  const { t } = useTranslation('goals');

  const isEditing = editingId === goal.id;

  const wasRecentlyMoved = recentlyMovedGoalId === goal.id;

  const isActiveGoal = activeGoal && activeGoal.id === goal.id;
  const goalNumber = isActiveGoal
    ? currentTargetPosition?.index + 1 || index + 1
    : index + 1;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: goal.id,
    data: {
      type: 'goal',
      goal,
      index,
      year,
    },
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    margin: 0,
    padding: 0,
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Prevent dragging when interacting with action buttons
  const handleActionButtonsPointerDown = e => {
    e.stopPropagation();
  };

  // Prevent dragging when interacting with text inputs
  const handleInputPointerDown = e => {
    e.stopPropagation();
  };

  return (
    <Table.Row
      ref={setNodeRef}
      style={{
        ...style,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      className={wasRecentlyMoved ? styles.goalRowYearChanged : ''}
      {...attributes}
      {...listeners}
    >
      <Table.Cell width="15px" pl="5" style={{ verticalAlign: 'middle' }}>
        <i
          className="fa-thin fa-grip-vertical"
          style={{ color: 'var(--gray-9)', marginTop: 'var(--space-2)' }}
        />
      </Table.Cell>

      <Table.Cell width="10px" pr="0" style={{ verticalAlign: 'middle' }}>
        <Text size="2" weight="bold">
          {goalNumber}.
        </Text>
      </Table.Cell>

      <Table.Cell width="100%" pl="2" style={{ verticalAlign: 'middle' }}>
        {isEditing ? (
          <Flex gap="2" width="100%">
            <AutoExpandTextarea
              value={editData.goal || ''}
              onChange={e => setEditData({ ...editData, goal: e.target.value })}
              onKeyDown={handleKeyDown}
              onPointerDown={handleInputPointerDown}
              setCursorToEnd={isEditing}
              autoFocus
            />
            <Text size="1" mt="2">
              {t('year')}:
            </Text>
            <Box width="110px">
              <TextField
                type="number"
                value={editData.planYear || ''}
                onChange={e =>
                  setEditData({
                    ...editData,
                    planYear: parseInt(e.target.value, 10),
                  })
                }
                onKeyDown={handleKeyDown}
                onPointerDown={handleInputPointerDown}
                className={styles.yearInputField}
              />
            </Box>
          </Flex>
        ) : (
          <Flex gap="2" width="100%">
            <Text size="2" weight="medium">
              {goal.goal}
            </Text>
          </Flex>
        )}
      </Table.Cell>

      <Table.Cell
        style={{ verticalAlign: 'middle' }}
        onPointerDown={handleActionButtonsPointerDown}
      >
        <ActionButtons
          isEditing={isEditing}
          onEdit={() => startEditing(goal)}
          onSave={saveEdit}
          onCancel={cancelEdit}
          onCopy={() => copyGoal(goal.id)}
          onDelete={() => deleteGoal(goal)}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default GoalRow;
